import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Single code evolution nodes - detailed evolution process with C++
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisSingleCodePositions');
  if (savedPositions) {
    try {
      const parsed = JSON.parse(savedPositions);
      // Check if this is the old structure (look for old Python filenames)
      const hasOldStructure = parsed.some(node => 
        node.data?.filename?.includes('.py') || 
        node.id === 'gpt4' || 
        node.id === 'claude' ||
        node.data?.filename === 'slow_algorithm.py'
      );
      
      if (hasOldStructure) {
        // Clear old cache and return new structure
        localStorage.removeItem('artemisSingleCodePositions');
        console.log('Cleared old diagram cache - loading new C++ evolution');
      } else {
        // Return cached positions if they're from the new structure
        return parsed;
      }
    } catch (e) {
      // If parsing fails, clear cache
      localStorage.removeItem('artemisSingleCodePositions');
    }
  }
  
  return [
    // Original problematic code - centered at top
    {
      id: 'original-code',
      type: 'collapsibleCode',
      data: { 
        filename: 'slow_search.cpp',
        code: `// Original O(n²) search algorithm
#include <vector>
#include <iostream>

std::vector<int> findDuplicates(const std::vector<int>& arr) {
    // Performance bottleneck: nested loops
    std::vector<int> duplicates;
    for (int i = 0; i < arr.size(); i++) {
        for (int j = i + 1; j < arr.size(); j++) {
            if (arr[i] == arr[j]) {
                // Check if already added
                bool found = false;
                for (int k = 0; k < duplicates.size(); k++) {
                    if (duplicates[k] == arr[i]) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    duplicates.push_back(arr[i]);
                }
            }
        }
    }
    return duplicates;
}`,
        issues: 'O(n³) complexity with nested loops and linear search',
        score: 1.8
      },
      position: { x: 600, y: 50 },
    },
    
    // AI Analysis - below original code
    {
      id: 'ai-analysis',
      data: { label: '🔍 Static Analysis\n• Complexity: O(n³)\n• Memory: Inefficient vectors\n• Cache misses: High\n• STL usage: Poor' },
      position: { x: 600, y: 250 },
      style: { ...themeStyles.analysisNode, width: 200 },
    },
    
    // LLM Agents label
    {
      id: 'llm-label',
      data: { label: '🤖 AI Code Evolution Agents' },
      position: { x: 600, y: 380 },
      style: { ...themeStyles.metricNode, width: 220 },
    },
    
    // Three LLM agents horizontally aligned
    {
      id: 'performance-agent',
      data: { label: '⚡ Performance Expert\n\n📝 Prompt:\n"Optimize this C++ code for speed.\nFocus on:\n• Algorithm complexity\n• Memory allocation\n• STL containers\n• Cache efficiency"' },
      position: { x: 250, y: 480 },
      style: { ...themeStyles.llmNode, width: 200, height: 140 },
    },
    {
      id: 'modern-cpp-agent',
      data: { label: '🔧 Modern C++ Expert\n\n📝 Prompt:\n"Rewrite using C++17/20:\n• Range-based algorithms\n• Smart pointers\n• Move semantics\n• Template optimizations"' },
      position: { x: 600, y: 480 },
      style: { ...themeStyles.llmNode, width: 200, height: 140 },
    },
    {
      id: 'memory-agent',
      data: { label: '💾 Memory Optimizer\n\n📝 Prompt:\n"Minimize memory usage:\n• In-place operations\n• Reserve capacity\n• Avoid allocations\n• Memory-friendly patterns"' },
      position: { x: 950, y: 480 },
      style: { ...themeStyles.llmNode, width: 200, height: 140 },
    },
    
    // Scoring system - moved to right side
    {
      id: 'scoring-root',
      data: { label: '🎯 Multi-Dimensional Code Scoring' },
      position: { x: 1350, y: 250 },
      style: { ...themeStyles.analysisNode, width: 240 },
    },
    
    // Performance Branch
    {
      id: 'performance-branch',
      data: { label: '⚡ Performance Metrics' },
      position: { x: 1250, y: 350 },
      style: { ...themeStyles.metricNode, width: 180 },
    },
    {
      id: 'time-complexity',
      data: { label: '⏱️ Time Complexity\nCurrent: O(n³)\nTarget: O(n log n)' },
      position: { x: 1100, y: 450 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'space-complexity',
      data: { label: '💽 Space Complexity\nCurrent: O(n)\nOptimal: O(n)' },
      position: { x: 1250, y: 450 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'cache-efficiency',
      data: { label: '🏎️ Cache Performance\nMisses: High\nLocality: Poor' },
      position: { x: 1400, y: 450 },
      style: themeStyles.suggestionNode,
    },
    
    // Code Quality Branch
    {
      id: 'quality-branch',
      data: { label: '📊 Code Quality' },
      position: { x: 1450, y: 350 },
      style: { ...themeStyles.metricNode, width: 180 },
    },
    {
      id: 'readability',
      data: { label: '📖 Readability\nScore: 2/5\nComplex logic' },
      position: { x: 1300, y: 550 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'maintainability',
      data: { label: '🔧 Maintainability\nScore: 2/5\nHard to modify' },
      position: { x: 1450, y: 550 },
      style: themeStyles.suggestionNode,
    },
    {
      id: 'cpp-standards',
      data: { label: '⭐ Modern C++\nScore: 1/5\nC++98 style' },
      position: { x: 1600, y: 550 },
      style: themeStyles.suggestionNode,
    },
    
    // Generation 1 - Initial suggestions
    {
      id: 'gen1-label',
      data: { label: '🌱 Generation 1: Initial Population (20 variants)' },
      position: { x: 600, y: 700 },
      style: { ...themeStyles.metricNode, width: 280 },
    },
    {
      id: 'suggestion1',
      type: 'collapsibleCode',
      data: { 
        filename: 'gen1_set.cpp',
        code: `// O(n) using unordered_set - Performance Agent
#include <vector>
#include <unordered_set>

std::vector<int> findDuplicates(const std::vector<int>& arr) {
    std::unordered_set<int> seen;
    std::unordered_set<int> duplicates;
    
    for (const int& num : arr) {
        if (seen.count(num)) {
            duplicates.insert(num);
        } else {
            seen.insert(num);
        }
    }
    
    return std::vector<int>(duplicates.begin(), duplicates.end());
}`,
        score: 3.7
      },
      position: { x: 250, y: 820 },
    },
    {
      id: 'suggestion2',
      type: 'collapsibleCode',
      data: { 
        filename: 'gen1_modern.cpp',
        code: `// Modern C++17 approach - Modern C++ Agent
#include <vector>
#include <unordered_map>
#include <algorithm>

auto findDuplicates(const std::vector<int>& arr) {
    std::unordered_map<int, size_t> counts;
    
    // Count occurrences
    for (const auto& num : arr) {
        ++counts[num];
    }
    
    // Extract duplicates using modern algorithms
    std::vector<int> result;
    std::copy_if(arr.begin(), arr.end(), 
                 std::back_inserter(result),
                 [&counts](int n) { return counts[n] > 1; });
    
    return result;
}`,
        score: 4.1
      },
      position: { x: 600, y: 820 },
    },
    {
      id: 'suggestion3',
      type: 'collapsibleCode',
      data: { 
        filename: 'gen1_memory.cpp',
        code: `// Memory optimized - Memory Agent
#include <vector>
#include <algorithm>

std::vector<int> findDuplicates(std::vector<int> arr) {
    // In-place solution to minimize memory
    std::sort(arr.begin(), arr.end());
    
    std::vector<int> duplicates;
    duplicates.reserve(arr.size() / 4); // Pre-allocate
    
    for (size_t i = 1; i < arr.size(); ++i) {
        if (arr[i] == arr[i-1] && 
            (duplicates.empty() || duplicates.back() != arr[i])) {
            duplicates.push_back(arr[i]);
        }
    }
    
    return duplicates;
}`,
        score: 3.9
      },
      position: { x: 950, y: 820 },
    },
    
    // Genetic Operations with Detailed Prompts
    {
      id: 'mutation-process',
      data: { label: '🧬 Mutation Operation\n\n📝 Prompt:\n"Apply random mutations:\n• Change container types\n• Modify loop structures\n• Add optimizations\n• Vary memory patterns\n\nMutation Rate: 15%"' },
      position: { x: 250, y: 1050 },
      style: { ...themeStyles.uniqueFeatureNode, width: 280, height: 200, fontSize: '11px' },
    },
    {
      id: 'crossover-process',
      data: { label: '🔀 Crossover Operation\n\n📝 Prompt:\n"Combine best features:\n• Take O(n) complexity from A\n• Use modern C++ syntax from B\n• Apply memory optimization from C\n• Merge error handling\n\nCrossover Rate: 70%"' },
      position: { x: 600, y: 1050 },
      style: { ...themeStyles.uniqueFeatureNode, width: 280, height: 200, fontSize: '11px' },
    },
    {
      id: 'selection-process',
      data: { label: '🏆 Selection Process\n\n📝 Prompt:\n"Select elite solutions:\n• Top 30% by performance\n• 20% by code quality\n• 10% for diversity\n• Tournament selection\n\nElitism: Keep top 5"' },
      position: { x: 950, y: 1050 },
      style: { ...themeStyles.uniqueFeatureNode, width: 280, height: 200, fontSize: '11px' },
    },
    
    // Evolution Process
    {
      id: 'evolution-process',
      type: 'evolution',
      data: { 
        generation: '1→2',
        description: 'Genetic operators applied to population',
        scores: {
          'Best Score': '4.1/5',
          'Avg Score': '3.6/5',
          'Diversity': 'High',
          'Convergence': '23%'
        }
      },
      position: { x: 600, y: 1320 },
    },
    
    // Generation 2 - Evolved
    {
      id: 'gen2-label',
      data: { label: '🧬 Generation 2: Evolved Solutions (Crossover Results)' },
      position: { x: 600, y: 1480 },
      style: { ...themeStyles.metricNode, width: 300 },
    },
    {
      id: 'evolved1',
      type: 'collapsibleCode',
      data: { 
        filename: 'gen2_hybrid.cpp',
        code: `// Crossover: Performance + Modern C++
#include <vector>
#include <unordered_set>
#include <algorithm>
#include <memory>

template<typename T>
auto findDuplicates(const std::vector<T>& arr) 
    -> std::vector<T> {
    
    if (arr.size() < 2) return {};
    
    // Reserve for efficiency (Memory Agent trait)
    std::unordered_set<T> seen;
    std::unordered_set<T> duplicates;
    seen.reserve(arr.size());
    
    // Modern range-based loop (Modern C++ trait)
    for (const auto& element : arr) {
        auto [iter, inserted] = seen.insert(element);
        if (!inserted) {
            duplicates.insert(element);
        }
    }
    
    // Convert back efficiently (Performance trait)
    std::vector<T> result;
    result.reserve(duplicates.size());
    std::move(duplicates.begin(), duplicates.end(), 
              std::back_inserter(result));
    
    return result;
}`,
        score: 4.6
      },
      position: { x: 400, y: 1600 },
    },
    {
      id: 'evolved2',
      type: 'collapsibleCode',
      data: { 
        filename: 'gen2_optimized.cpp',
        code: `// Mutation: Cache-optimized variant
#include <vector>
#include <algorithm>
#include <execution>

std::vector<int> findDuplicates(std::vector<int> arr) {
    if (arr.empty()) return {};
    
    // Parallel sort for large datasets (Mutation)
    std::sort(std::execution::par_unseq, 
              arr.begin(), arr.end());
    
    std::vector<int> result;
    result.reserve(arr.size() / 8); // Heuristic allocation
    
    // Cache-friendly sequential access
    for (auto it = arr.begin(); it != arr.end(); ++it) {
        auto next = std::next(it);
        if (next != arr.end() && *it == *next) {
            // Skip to end of duplicate sequence
            auto range_end = std::upper_bound(it, arr.end(), *it);
            if (result.empty() || result.back() != *it) {
                result.push_back(*it);
            }
            it = std::prev(range_end);
        }
    }
    
    return result;
}`,
        score: 4.4
      },
      position: { x: 800, y: 1600 },
    },
    
    // Final Evolution
    {
      id: 'final-evolution',
      type: 'evolution',
      data: { 
        generation: 'Final',
        description: 'Elite selection after 3 generations',
        scores: {
          'Performance': '4.9/5',
          'Memory': '4.7/5',
          'Readability': '4.8/5',
          'Modern C++': '4.9/5'
        }
      },
      position: { x: 600, y: 1850 },
    },
    
    // Winner
    {
      id: 'winner',
      type: 'collapsibleCode',
      data: { 
        filename: 'final_solution.cpp',
        code: `// Final evolved solution - Production ready
#include <vector>
#include <unordered_set>
#include <algorithm>
#include <concepts>

template<std::equality_comparable T>
[[nodiscard]] constexpr auto findDuplicates(
    const std::vector<T>& arr) noexcept -> std::vector<T> {
    
    if (arr.size() < 2) [[likely]] return {};
    
    // Optimized containers with perfect sizing
    std::unordered_set<T> seen;
    std::unordered_set<T> duplicates;
    seen.reserve(arr.size());
    duplicates.reserve(arr.size() >> 3); // Estimate
    
    // Branchless duplicate detection
    for (const auto& element : arr) {
        const auto [_, is_new] = seen.insert(element);
        if (!is_new) [[unlikely]] {
            duplicates.insert(element);
        }
    }
    
    // Zero-copy when possible
    if (duplicates.empty()) [[likely]] return {};
    
    std::vector<T> result;
    result.reserve(duplicates.size());
    std::ranges::move(duplicates, std::back_inserter(result));
    
    return result;
}`,
        score: 4.9
      },
      position: { x: 600, y: 2000 },
    },
    
    // Performance Comparison - left side
    {
      id: 'performance-stats',
      data: { label: '📊 Performance Evolution\n\n• Complexity: O(n³) → O(n)\n• Memory: 3x reduction\n• Cache misses: 85% fewer\n• C++ standard: C++98 → C++20\n• Execution time: 2000ms → 15ms' },
      position: { x: 250, y: 2150 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    },
    
    // Code Quality Metrics - right side
    {
      id: 'quality-stats',
      data: { label: '⭐ Quality Improvement\n\n• Maintainability: +240%\n• Readability: +167%\n• Type safety: Perfect\n• Exception safety: Strong\n• Test coverage: 98%' },
      position: { x: 950, y: 2150 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250, height: 140 },
    }
  ];
};

const evolutionEdges = [
  // Initial flow - top to bottom
  { id: 'e1', source: 'original-code', target: 'ai-analysis', animated: true, style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e2', source: 'ai-analysis', target: 'llm-label', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // LLM agents - vertical connections
  { id: 'e3', source: 'llm-label', target: 'performance-agent', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e4', source: 'llm-label', target: 'modern-cpp-agent', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e5', source: 'llm-label', target: 'memory-agent', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // Scoring system - side connection
  { id: 'e6', source: 'ai-analysis', target: 'scoring-root', animated: true, style: themeStyles.edgeStyle, type: 'bezier' },
  { id: 'e7', source: 'scoring-root', target: 'performance-branch', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e8', source: 'scoring-root', target: 'quality-branch', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // Performance metrics
  { id: 'e9', source: 'performance-branch', target: 'time-complexity', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e10', source: 'performance-branch', target: 'space-complexity', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e11', source: 'performance-branch', target: 'cache-efficiency', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // Quality metrics
  { id: 'e12', source: 'quality-branch', target: 'readability', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e13', source: 'quality-branch', target: 'maintainability', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e14', source: 'quality-branch', target: 'cpp-standards', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // Agents to Generation 1 label
  { id: 'e15a', source: 'performance-agent', target: 'gen1-label', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e15b', source: 'modern-cpp-agent', target: 'gen1-label', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e15c', source: 'memory-agent', target: 'gen1-label', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // Generation 1 label to suggestions
  { id: 'e16', source: 'gen1-label', target: 'suggestion1', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e17', source: 'gen1-label', target: 'suggestion2', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e18', source: 'gen1-label', target: 'suggestion3', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // Suggestions to genetic operations
  { id: 'e19', source: 'suggestion1', target: 'mutation-process', style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  { id: 'e20', source: 'suggestion2', target: 'crossover-process', animated: true, style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  { id: 'e21', source: 'suggestion3', target: 'selection-process', style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  
  // Genetic operations to evolution process
  { id: 'e22', source: 'mutation-process', target: 'evolution-process', style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  { id: 'e23', source: 'crossover-process', target: 'evolution-process', animated: true, style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  { id: 'e24', source: 'selection-process', target: 'evolution-process', style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  
  // Scoring feedback to evolution
  { id: 'f1', source: 'performance-branch', target: 'evolution-process', style: themeStyles.feedbackEdgeStyle, type: 'bezier' },
  { id: 'f2', source: 'quality-branch', target: 'evolution-process', style: themeStyles.feedbackEdgeStyle, type: 'bezier' },
  
  // Evolution to Gen 2 label
  { id: 'e25', source: 'evolution-process', target: 'gen2-label', animated: true, style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  
  // Gen 2 label to evolved solutions
  { id: 'e26', source: 'gen2-label', target: 'evolved1', animated: true, style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  { id: 'e27', source: 'gen2-label', target: 'evolved2', animated: true, style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  
  // Evolved solutions to final evolution
  { id: 'e28', source: 'evolved1', target: 'final-evolution', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e29', source: 'evolved2', target: 'final-evolution', style: themeStyles.edgeStyle, type: 'smoothstep' },
  
  // Final evolution to winner
  { id: 'e30', source: 'final-evolution', target: 'winner', animated: true, style: themeStyles.evolutionEdgeStyle, type: 'smoothstep' },
  
  // Winner to stats
  { id: 'e31', source: 'winner', target: 'performance-stats', style: themeStyles.edgeStyle, type: 'smoothstep' },
  { id: 'e32', source: 'winner', target: 'quality-stats', style: themeStyles.edgeStyle, type: 'smoothstep' },
];

const ArtemisSingleCodeEvolution = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(evolutionEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      // Save positions to localStorage
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style,
        type: node.type,
        data: node.data
      }));
      localStorage.setItem('artemisSingleCodePositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);

  // Reset positions
  const resetPositions = () => {
    localStorage.removeItem('artemisSingleCodePositions');
    setNodes(getInitialNodes());
  };

  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#212529', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🧬 Single C++ Code Evolution - Genetic Programming
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(33, 37, 41, 0.08)',
          borderRadius: '20px',
          color: '#495057',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100,
          maxWidth: '600px',
          textAlign: 'center'
        }}>
          Watch how genetic algorithms evolve a slow C++ function through mutation, crossover, and selection
        </div>

        {/* Reset button */}
        <button
          onClick={resetPositions}
          style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            padding: '8px 16px',
            background: '#6C757D',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
            cursor: 'pointer',
            zIndex: 100,
            transition: 'background 0.2s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = '#5C636A'}
          onMouseLeave={(e) => e.target.style.background = '#6C757D'}
        >
          Reset Layout
        </button>
        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.1,
              minZoom: 0.2,
              maxZoom: 1.0
            }}
            minZoom={0.15}
            maxZoom={1.5}
            defaultViewport={{ x: -100, y: 0, zoom: 0.35 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(255,255,255,0.8)',
                border: '1px solid #DEE2E6'
              }}
              nodeColor={(node) => {
                return node.style?.background || '#6C757D';
              }}
              nodeStrokeColor={() => '#495057'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#DEE2E6" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Genetic Algorithm Indicators */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          right: '20px',
          background: 'rgba(255,255,255,0.95)',
          padding: '15px',
          borderRadius: '15px',
          border: '1px solid #DEE2E6',
          color: '#212529',
          fontSize: '13px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          maxWidth: '300px'
        }}>
          <div style={{ marginBottom: '10px', fontWeight: '600', color: '#6F42C1' }}>🧬 Genetic Programming</div>
          <div style={{ marginBottom: '5px' }}>Original: <span style={{ color: '#DC3545' }}>O(n³) - 2000ms</span></div>
          <div style={{ marginBottom: '5px' }}>Generation 1: <span style={{ color: '#FD7E14' }}>O(n) - 100ms</span></div>
          <div style={{ marginBottom: '5px' }}>Generation 2: <span style={{ color: '#28A745' }}>O(n) - 15ms</span></div>
          <div style={{ marginBottom: '10px' }}>Final: <span style={{ color: '#20C997', fontWeight: '600' }}>O(n) - 12ms</span></div>
          <div style={{ fontSize: '12px', color: '#6C757D', lineHeight: '1.4' }}>
            <strong>Genetic Operators:</strong><br/>
            • Mutation: Random code changes<br/>
            • Crossover: Combine best features<br/>
            • Selection: Keep elite solutions
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisSingleCodeEvolution; 